// src/events.ts
var EventEmitter = class {
  listeners = /* @__PURE__ */ new Map();
  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, /* @__PURE__ */ new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }
  /**
   * Unsubscribe from an event
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.listeners.delete(event);
      }
    }
  }
  /**
   * Subscribe to an event only once
   * @returns Unsubscribe function
   */
  once(event, callback) {
    const onceCallback = (...args) => {
      this.off(event, onceCallback);
      callback(...args);
    };
    return this.on(event, onceCallback);
  }
  /**
   * Emit an event to all subscribers
   */
  emit(event, ...args) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event handler for "${event}":`, error);
        }
      });
    }
  }
  /**
   * Remove all listeners for an event, or all events if no event specified
   */
  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
  /**
   * Get the number of listeners for an event
   */
  listenerCount(event) {
    return this.listeners.get(event)?.size ?? 0;
  }
};

// src/storage/memory.ts
function createMemoryStorage() {
  const store = /* @__PURE__ */ new Map();
  return {
    async get(key) {
      const value = store.get(key);
      return value ?? null;
    },
    async set(key, value) {
      store.set(key, value);
    },
    async remove(key) {
      store.delete(key);
    },
    async clear() {
      store.clear();
    }
  };
}
var memoryStorage = createMemoryStorage();

// src/storage/localStorage.ts
function isLocalStorageAvailable() {
  try {
    const testKey = "__godgpt_storage_test__";
    if (typeof window === "undefined" || !window.localStorage) {
      return false;
    }
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
function createLocalStorage(prefix = "godgpt") {
  const getKey = (key) => `${prefix}:${key}`;
  if (!isLocalStorageAvailable()) {
    console.warn(
      "localStorage is not available, falling back to memory storage"
    );
    const store = /* @__PURE__ */ new Map();
    return {
      async get(key) {
        return store.get(key) ?? null;
      },
      async set(key, value) {
        store.set(key, value);
      },
      async remove(key) {
        store.delete(key);
      },
      async clear() {
        store.clear();
      }
    };
  }
  return {
    async get(key) {
      try {
        const item = window.localStorage.getItem(getKey(key));
        if (item === null) return null;
        return JSON.parse(item);
      } catch {
        return null;
      }
    },
    async set(key, value) {
      try {
        window.localStorage.setItem(getKey(key), JSON.stringify(value));
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }
    },
    async remove(key) {
      try {
        window.localStorage.removeItem(getKey(key));
      } catch (error) {
        console.error("Failed to remove from localStorage:", error);
      }
    },
    async clear() {
      try {
        const keysToRemove = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key?.startsWith(`${prefix}:`)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => window.localStorage.removeItem(key));
      } catch (error) {
        console.error("Failed to clear localStorage:", error);
      }
    }
  };
}
var localStorageAdapter = createLocalStorage();

// src/storage/asyncStorage.ts
function createAsyncStorageAdapter(asyncStorage, prefix = "godgpt") {
  const getKey = (key) => `${prefix}:${key}`;
  return {
    async get(key) {
      try {
        const item = await asyncStorage.getItem(getKey(key));
        if (item === null) return null;
        return JSON.parse(item);
      } catch {
        return null;
      }
    },
    async set(key, value) {
      try {
        await asyncStorage.setItem(getKey(key), JSON.stringify(value));
      } catch (error) {
        console.error("Failed to save to AsyncStorage:", error);
      }
    },
    async remove(key) {
      try {
        await asyncStorage.removeItem(getKey(key));
      } catch (error) {
        console.error("Failed to remove from AsyncStorage:", error);
      }
    },
    async clear() {
      try {
        if (asyncStorage.getAllKeys && asyncStorage.multiRemove) {
          const allKeys = await asyncStorage.getAllKeys();
          const keysToRemove = allKeys.filter(
            (key) => key.startsWith(`${prefix}:`)
          );
          if (keysToRemove.length > 0) {
            await asyncStorage.multiRemove(keysToRemove);
          }
        } else if (asyncStorage.clear) {
          await asyncStorage.clear();
        }
      } catch (error) {
        console.error("Failed to clear AsyncStorage:", error);
      }
    }
  };
}

// src/context.ts
function createLogger(debug) {
  const noop = () => {
  };
  if (!debug) {
    return {
      debug: noop,
      info: noop,
      warn: console.warn.bind(console),
      error: console.error.bind(console)
    };
  }
  return {
    debug: (...args) => console.log("[GodGPT:DEBUG]", ...args),
    info: (...args) => console.info("[GodGPT:INFO]", ...args),
    warn: (...args) => console.warn("[GodGPT:WARN]", ...args),
    error: (...args) => console.error("[GodGPT:ERROR]", ...args)
  };
}
var SDKContext = class {
  config;
  events;
  storage;
  logger;
  modules = /* @__PURE__ */ new Map();
  initialized = false;
  constructor(config = {}) {
    this.config = {
      debug: false,
      ...config
    };
    this.events = new EventEmitter();
    this.storage = config.storage ?? memoryStorage;
    this.logger = createLogger(this.config.debug ?? false);
  }
  /**
   * Register a module with the SDK
   */
  register(name, module) {
    if (this.modules.has(name)) {
      this.logger.warn(`Module "${name}" is already registered, replacing...`);
    }
    this.modules.set(name, module);
    this.logger.debug(`Module "${name}" registered`);
  }
  /**
   * Get a registered module by name
   */
  get(name) {
    return this.modules.get(name);
  }
  /**
   * Check if a module is registered
   */
  has(name) {
    return this.modules.has(name);
  }
  /**
   * Initialize all registered modules
   */
  async init() {
    if (this.initialized) {
      this.logger.warn("SDK already initialized");
      return;
    }
    this.logger.debug("Initializing SDK...");
    for (const [name, module] of this.modules) {
      this.logger.debug(`Initializing module: ${name}`);
      try {
        await module.init(this);
        this.logger.debug(`Module "${name}" initialized`);
      } catch (error) {
        this.logger.error(`Failed to initialize module "${name}":`, error);
        throw error;
      }
    }
    this.initialized = true;
    this.events.emit("sdk:ready");
    this.logger.debug("SDK initialized successfully");
  }
  /**
   * Destroy all modules and cleanup
   */
  async destroy() {
    this.logger.debug("Destroying SDK...");
    const moduleEntries = Array.from(this.modules.entries()).reverse();
    for (const [name, module] of moduleEntries) {
      this.logger.debug(`Destroying module: ${name}`);
      try {
        await module.destroy();
        this.logger.debug(`Module "${name}" destroyed`);
      } catch (error) {
        this.logger.error(`Failed to destroy module "${name}":`, error);
      }
    }
    this.modules.clear();
    this.events.removeAllListeners();
    this.initialized = false;
    this.logger.debug("SDK destroyed");
  }
  /**
   * Check if SDK is initialized
   */
  isInitialized() {
    return this.initialized;
  }
  /**
   * Get all registered module names
   */
  getModuleNames() {
    return Array.from(this.modules.keys());
  }
};

// src/sdk.ts
function createSDK(config = {}) {
  const context = new SDKContext(config);
  return {
    use(module) {
      context.register(module.name, module);
      return module;
    },
    async init() {
      await context.init();
    },
    get(name) {
      return context.get(name);
    },
    has(name) {
      return context.has(name);
    },
    on(event, callback) {
      return context.events.on(event, callback);
    },
    off(event, callback) {
      context.events.off(event, callback);
    },
    emit(event, ...args) {
      context.events.emit(event, ...args);
    },
    async destroy() {
      await context.destroy();
    },
    isInitialized() {
      return context.isInitialized();
    },
    get storage() {
      return context.storage;
    },
    get config() {
      return context.config;
    }
  };
}

export { EventEmitter, SDKContext as SDKContextImpl, createAsyncStorageAdapter, createLocalStorage, createMemoryStorage, createSDK, localStorageAdapter, memoryStorage };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map